import React, { useCallback, useEffect, useRef, useState } from 'react'
import SidePanel from '../../../../ui/SidePanel/SidePanel'
import Spinner from '../../../../ui/Spinner/Spinner'
import { isNull, orderBy } from 'lodash'
import { withRouter } from 'react-router-dom'
import { db } from '../../../../config/firebase'
import Button from '../../../../ui/Button/Button'
import BannerManager from './AddBanner/AddBanner'
import './BannersList.sass'
import InfoLine from '../../../../ui/InfoLine/InfoLine'
import SortableList from '../../../../ui/SortableList/SortableList'
import { arrayMove } from 'react-sortable-hoc'
import { updateDoc } from '../../../../utils/db/updateDoc'

const REORDER_SAVE_DELAY_MS = 500

function BannersList({ routerIdName, onClickPath, ...router }) {
  const [banners, setBanners] = useState(null)
  const bannerId = router.match.params[routerIdName]
  const [bannerManagerState, setBannerManagerState] = useState({
    type: null,
    bannerId: null,
  })

  const bannersRef = useRef(null)
  const pendingOrderRef = useRef(false)
  const saveTimeoutRef = useRef(null)
  const orderBeforeDragRef = useRef(null)

  const persistOrder = useCallback(async (orderedBanners) => {
    const results = await Promise.all(
      orderedBanners.map((banner, index) =>
        updateDoc({
          path: 'settings/banners/list',
          docId: banner.id,
          data: { pos: index },
        })
      )
    )

    if (results.some((result) => result === 'fail')) {
      throw new Error('Failed to save banner order')
    }
  }, [])

  useEffect(() => {
    const unsubscribe = db.collection('settings/banners/list').onSnapshot((snap) => {
      if (pendingOrderRef.current) return

      const data = []
      snap.forEach((doc) => data.push({ id: doc.id, ...doc.data() }))
      const bannersWithOrder = orderBy(data, ['pos'], ['asc'])
      bannersRef.current = bannersWithOrder
      setBanners(bannersWithOrder)
    })

    return () => {
      unsubscribe()

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }

      if (pendingOrderRef.current && bannersRef.current) {
        const ordered = bannersRef.current
        pendingOrderRef.current = false
        persistOrder(ordered).catch((err) => {
          console.error(err)
        })
      }
    }
  }, [persistOrder])

  const onClick = useCallback(
    (id) => {
      router.history.push(`${onClickPath}/${id}`)
    },
    [onClickPath, router.history]
  )

  useEffect(() => {
    if (!bannerId && !isNull(banners) && banners.length) {
      onClick(banners[0].id)
    }
  }, [bannerId, banners, onClick])

  const onAdd = () => {
    setBannerManagerState({
      type: 'add',
    })
  }

  const onBannerManagerClose = () => {
    setBannerManagerState({
      type: null,
      bannerId: null,
    })
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex || isNull(banners)) return

    if (!pendingOrderRef.current) {
      orderBeforeDragRef.current = banners
    }

    const nextBanners = arrayMove(banners, oldIndex, newIndex).map((banner, index) => ({
      ...banner,
      pos: index,
    }))

    pendingOrderRef.current = true
    bannersRef.current = nextBanners
    setBanners(nextBanners)

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null
      const ordered = bannersRef.current

      persistOrder(ordered)
        .then(() => {
          pendingOrderRef.current = false
          orderBeforeDragRef.current = null
        })
        .catch((err) => {
          console.error(err)
          if (orderBeforeDragRef.current) {
            bannersRef.current = orderBeforeDragRef.current
            setBanners(orderBeforeDragRef.current)
          }
          pendingOrderRef.current = false
        })
    }, REORDER_SAVE_DELAY_MS)
  }

  return (
    <>
      {bannerManagerState.type === 'add' && <BannerManager onClose={onBannerManagerClose} />}
      <SidePanel
        title="Серії банерів"
        content={
          <>
            {!isNull(banners) ? (
              <SortableList
                className="BannersList-Container"
                axis="y"
                onSortEnd={onSortEnd}
                pressDelay={200}
                items={banners.map((b) => (
                  <div
                    className={`Banner ${bannerId === b.id && 'Banner_theme_active'} ${
                      !b.isActive && 'Banner_theme_detached'
                    }`}
                    onClick={() => onClick(b.id)}
                  >
                    <span>#{b.pos}</span>
                    <div className="BannerContent-Data">
                      {b.name}
                      <InfoLine
                        title="Термін показу"
                        content={
                          b.isTerm ? `від ${b.dateStart}, ${b.timeStart} до ${b.dateEnd}, ${b.timeEnd}` : 'Без терміну'
                        }
                        className={'Term'}
                      />
                    </div>
                  </div>
                ))}
              />
            ) : (
              <Spinner type="popup" />
            )}
          </>
        }
        buttons={<Button theme="circle" fill="accent" icon="plus" onClick={onAdd} />}
      />
    </>
  )
}

export default withRouter(BannersList)
